import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';

import ChatPanel from './ChatPanel';

// localStorage key remembering the assistant panel's width (a per-browser convenience only).
const c_AssistantPanelWidthKey = 'aeoaudited-assistant-panel-width';

// The panel's width limits. It is never narrower than c_MinPanelWidth, and never so wide that the content beside
// it (to the right of the side menu) gets less than c_MinContentWidth.
const c_DefaultPanelWidth = 440;
const c_MinPanelWidth = 320;
const c_MinContentWidth = 360;
const c_SideMenuWidth = 230;
const c_KeyboardResizeStep = 20;

// The request Ask AI sends when it starts a new chat about the analysis on screen, for a single page analysis and
// for a full site analysis.
const c_AskAIQuestion = 'Analyze this analysis: explain the grade and each score, what is holding the page back, and which fixes to do first.';
const c_AskAISiteQuestion = 'Analyze this site analysis: explain the grade and each score, which pages are weakest and why, and which fixes to do first across the site.';

// The CSS variable App.css reads for the panel's width and the content's right margin.
const c_PanelWidthCssVariable = '--assistant-panel-width';

const clamp_panel_width = (width)=>{
	const max_width = Math.max(c_MinPanelWidth, window.innerWidth - c_SideMenuWidth - c_MinContentWidth);
	return Math.round(Math.min(Math.max(width, c_MinPanelWidth), max_width));
};

const read_panel_width = ()=>{
	try{
		const stored_width = parseInt(window.localStorage.getItem(c_AssistantPanelWidthKey), 10);
		return clamp_panel_width(isNaN(stored_width) ? c_DefaultPanelWidth : stored_width);
	}
	catch(error){
		return clamp_panel_width(c_DefaultPanelWidth);
	}
};

const store_panel_width = (width)=>{
	try{
		window.localStorage.setItem(c_AssistantPanelWidthKey, String(width));
	}
	catch(error){
		console.log('AssistantLayout/store_panel_width - ERROR: ', describe_error(error));
	}
};

// A page showing an analysis, with the resizable, collapsible AEO Assistant panel on the right.
// render_content(on_analysis_change, on_ask_ai) renders the page: on_analysis_change(analysis, kind) is told which
// analysis is on screen, kind 'site' for a full site analysis and otherwise a single page analysis, so "this page"
// or "this site" in the assistant means it; on_ask_ai is the Ask AI button's handler.
// view_key, when given, names the view the assistant remembers its chat under (the site page view gives
// site_page:<id>, so re-analyzing the page keeps its chat); otherwise it is the analysis on screen,
// site_analysis:<id> or page_analysis:<id>, and null until that analysis has loaded.
function AssistantLayout({render_content, view_key}){
	// The panel is always closed when a page opens: it opens only when the user opens it with the AEO Assistant
	// button or Ask AI.
	const [panel_open, set_panel_open] = useState(false);

	// The panel slides by CSS keyframe animation, which plays whenever its class is applied, whatever the render
	// timing. panel_motion is 'in' while it slides in after the user opens it, 'out' while it slides out after they
	// close it, and 'none' otherwise. The panel stays mounted until its slide out ends, see on_panel_animation_end.
	const [panel_mounted, set_panel_mounted] = useState(false);
	const [panel_motion, set_panel_motion] = useState('none');
	const panel_opened_ref = useRef(false);

	useEffect(()=>{
		if(panel_open === panel_opened_ref.current){
			return;
		}

		panel_opened_ref.current = panel_open;

		if(panel_open){
			set_panel_mounted(true);
			set_panel_motion('in');
			return;
		}

		set_panel_motion('out');
	}, [panel_open]);

	// The panel's own slide has ended: after sliding out it is unmounted. Animations inside the panel (a loading
	// spinner, say) bubble up here too and are ignored.
	const on_panel_animation_end = (event)=>{
		if(event.target !== event.currentTarget){
			return;
		}

		if(panel_motion === 'out'){
			set_panel_mounted(false);
		}

		set_panel_motion('none');
	};
	const [panel_width, set_panel_width] = useState(read_panel_width());

	// The width being dragged, read by the window listeners while a resize is in progress.
	const panel_width_ref = useRef(panel_width);
	panel_width_ref.current = panel_width;

	// Publish the width to App.css, which sizes the panel and the content's right margin from it.
	useEffect(()=>{
		document.documentElement.style.setProperty(c_PanelWidthCssVariable, panel_width + 'px');
	}, [panel_width]);

	// Keep the width within limits when the window is resized, and remove the variable when leaving the page.
	useEffect(()=>{
		const on_window_resize = ()=>{
			set_panel_width((current)=>{ return clamp_panel_width(current); });
		};

		window.addEventListener('resize', on_window_resize);

		return ()=>{
			window.removeEventListener('resize', on_window_resize);
			document.documentElement.style.removeProperty(c_PanelWidthCssVariable);
		};
	}, []);

	// Drag the panel's left edge: the panel spans from the pointer to the right edge of the window.
	const start_resize = (event)=>{
		event.preventDefault();
		document.body.classList.add('assistant-panel-resizing');

		const on_pointer_move = (move_event)=>{
			set_panel_width(clamp_panel_width(window.innerWidth - move_event.clientX));
		};

		const on_pointer_up = ()=>{
			document.body.classList.remove('assistant-panel-resizing');
			window.removeEventListener('pointermove', on_pointer_move);
			window.removeEventListener('pointerup', on_pointer_up);
			store_panel_width(panel_width_ref.current);
		};

		window.addEventListener('pointermove', on_pointer_move);
		window.addEventListener('pointerup', on_pointer_up);
	};

	// The resize handle also works from the keyboard: Left widens the panel, Right narrows it.
	const on_resize_key_down = (event)=>{
		var next_width = null;

		if(event.key === 'ArrowLeft'){
			next_width = clamp_panel_width(panel_width + c_KeyboardResizeStep);
		}
		else
		if(event.key === 'ArrowRight'){
			next_width = clamp_panel_width(panel_width - c_KeyboardResizeStep);
		}

		if(next_width === null){
			return;
		}

		event.preventDefault();
		set_panel_width(next_width);
		store_panel_width(next_width);
	};

	// The analysis on screen, passed to the assistant so "this page" means it.
	const [analysis_context, set_analysis_context] = useState(null);

	// A question the Ask AI button has handed to the assistant, sent by ChatPanel once its chat is ready. key is new
	// on every click, so asking twice sends twice.
	const [pending_question, set_pending_question] = useState(null);

	const set_panel_state = (open)=>{
		set_panel_open(open);
	};

	const on_analysis_change = (analysis, kind)=>{
		if(analysis && analysis.id && analysis.url){
			set_analysis_context({ analysis_id: analysis.id, url: analysis.url, kind: (kind === 'site') ? 'site' : 'page' });
		}
		else{
			set_analysis_context(null);
		}
	};

	// Ask AI: open the assistant on the user's chat about the analysis on screen, or start one that analyzes it.
	const on_ask_ai = ()=>{
		if(!analysis_context){
			return;
		}

		set_panel_state(true);
		const question = (analysis_context.kind === 'site') ? c_AskAISiteQuestion : c_AskAIQuestion;
		set_pending_question({ key: Date.now(), analysis_id: analysis_context.analysis_id, kind: analysis_context.kind, text: question });
	};

	// The toggle stays mounted and slides off the right edge while the panel is open.
	const render_panel_toggle = ()=>{
		const toggle_class = panel_open ? 'assistant-panel-toggle assistant-panel-toggle-hidden' : 'assistant-panel-toggle';

		return (
			<button
				type="button"
				className={toggle_class}
				onClick={()=>{ set_panel_state(true); }}
				aria-label="Open the AEO Assistant"
				aria-hidden={panel_open}
				tabIndex={panel_open ? -1 : 0}
			>
				AEO Assistant
			</button>
		);
	};

	const render_panel = ()=>{
		if(!panel_mounted){
			return null;
		}

		let panel_class = 'assistant-panel';
		if(panel_motion === 'in'){
			panel_class += ' assistant-panel-sliding-in';
		}
		else
		if(panel_motion === 'out'){
			panel_class += ' assistant-panel-sliding-out';
		}

		return (
			<aside className={panel_class} onAnimationEnd={on_panel_animation_end}>
				<div
					className="assistant-panel-resize-handle"
					role="separator"
					aria-orientation="vertical"
					aria-label="Resize the AEO Assistant"
					aria-valuenow={panel_width}
					aria-valuemin={c_MinPanelWidth}
					tabIndex={0}
					onPointerDown={start_resize}
					onKeyDown={on_resize_key_down}
				/>
				<ChatPanel
					view_key={get_view_key()}
					analysis_context={analysis_context}
					on_close={()=>{ set_panel_state(false); }}
					pending_message={pending_question}
					on_pending_message_sent={()=>{ set_pending_question(null); }}
				/>
			</aside>
		);
	};

	// The view the panel's chat is remembered under.
	const get_view_key = ()=>{
		if(view_key){
			return view_key;
		}

		if(!analysis_context){
			return null;
		}

		return ((analysis_context.kind === 'site') ? 'site_analysis:' : 'page_analysis:') + analysis_context.analysis_id;
	};

	const layout_class = panel_open ? 'analysis-layout analysis-layout-panel-open' : 'analysis-layout';

	return (
		<React.Fragment>
			<div className={layout_class}>
				{render_content(on_analysis_change, on_ask_ai)}
			</div>
			{render_panel_toggle()}
			{render_panel()}
		</React.Fragment>
	);
}

export default AssistantLayout;
